import { RedoOutlined as RedoIcon } from "@mui/icons-material";
import { Button, Paper, Stack, Typography } from "@mui/material";
import NextLink from "next/link";
import { useRouter } from "next/router";
import React, { useCallback, useContext, useMemo } from "react";

import { KycContext, KycContextProvider } from "../../contexts/kyc/KycContext";
import { IKycContextQuery, useKycContextQuery } from "../../contexts/kyc/KycContext.generated";
import { Permission } from "../../contexts/permission/Permission";
import { PermissionsContext } from "../../contexts/permission/PermissionsContext";
import { IApplicationStatus } from "../../generated/types";
import { IUseOperationContextOpts, useOperationContext } from "../../hooks/useOperationContext";
import { Routes } from "../../Routes";
import { NO_LEVRO_ORG_KEY_HEADERS_INIT } from "../../utils/getRequestHeaders";
import { visitQueryState } from "../../utils/visitQueryState";
import { CombinedErrorAlert } from "../combined-error-alert/CombinedErrorAlert";
import { LoadingLayout } from "../layout/LoadingLayout";
import { LogoutButton } from "../layout/LogoutButton";
import { NoAuthLayout } from "../layout/page/NoAuthLayout";
import { SwitchOrgButton } from "../layout/SwitchOrgButton";
import { Redirect } from "../Redirect";
import { ApplicationStatusPage } from "./FinalizeApplication";
import { KycApplication } from "./KycApplication";

const KYC_CONTEXT_OPERATION_CONTEXT_OPTS: IUseOperationContextOpts<IKycContextQuery> = {
    additionalTypenameFlags: { ApplicationDocument: true, DefaultError: false },
};

interface IProps {
    applicationId?: string;
    inviteCode?: string;
}

export const KycIncomplete: React.FC<IProps> = ({ applicationId, inviteCode }) => {
    const operationContext = useOperationContext(KYC_CONTEXT_OPERATION_CONTEXT_OPTS);
    const context = useMemo(
        () => ({ ...operationContext, fetchOptions: { headers: NO_LEVRO_ORG_KEY_HEADERS_INIT } }),
        [operationContext],
    );

    const [applicationQueryState, fetchApplication] = useKycContextQuery({
        variables: { applicationInput: applicationId == null ? undefined : { applicationId } },
        context,
    });
    const refetchApplication = useCallback(
        (): void => fetchApplication({ requestPolicy: "cache-and-network" }),
        [fetchApplication],
    );

    function renderHomeButton() {
        return (
            <NextLink passHref={true} href={Routes.Dashboard()} legacyBehavior={true}>
                <Button variant="contained" sx={{ alignSelf: "center" }}>
                    Return to home
                </Button>
            </NextLink>
        );
    }

    return (
        <NoAuthLayout
            title="Apply for an Account"
            actions={[<SwitchOrgButton key="switch-org-button" />, <LogoutButton key="logout-button" />]}
        >
            <Stack alignItems="center">
                <Stack maxWidth={700} spacing={4} p={3}>
                    <Paper sx={{ p: 8 }} variant="outlined">
                        {visitQueryState(applicationQueryState, {
                            notLoaded: () => <LoadingLayout />,
                            loaded: ({
                                data: {
                                    getApplication: { application, errors },
                                },
                            }) => {
                                if (errors != null) {
                                    return (
                                        <Stack spacing={2}>
                                            <CombinedErrorAlert title="Error loading application" gqlErrors={errors} />
                                            {renderHomeButton()}
                                        </Stack>
                                    );
                                }
                                return (
                                    <KycContextProvider
                                        unitApplication={application ?? undefined}
                                        refetchApplication={refetchApplication}
                                    >
                                        <LoadedKycIncomplete inviteCode={inviteCode} />
                                    </KycContextProvider>
                                );
                            },
                            error: ({ error }) => (
                                <Stack spacing={2}>
                                    <CombinedErrorAlert title="Error loading application" error={error} />
                                    {renderHomeButton()}
                                </Stack>
                            ),
                        })}
                    </Paper>
                </Stack>
            </Stack>
        </NoAuthLayout>
    );
};

interface ILoadedKycIncompleteProps {
    inviteCode?: string;
}

export const LoadedKycIncomplete: React.FC<ILoadedKycIncompleteProps> = ({ inviteCode }) => {
    const router = useRouter();
    const { unitApplication } = useContext(KycContext);
    const { hasPermission } = useContext(PermissionsContext);

    if (unitApplication == null) {
        return (
            <Stack alignItems="center" justifyContent="center" spacing={2}>
                <Typography>Error loading application</Typography>
                <Button onClick={router.reload} startIcon={<RedoIcon />} variant="contained">
                    Try again
                </Button>
            </Stack>
        );
    }

    const { status } = unitApplication;
    switch (status) {
        case IApplicationStatus.New: {
            return <KycApplication inviteCode={inviteCode} />;
        }
        case IApplicationStatus.AccountSetup: {
            if (hasPermission(Permission.ReadOrgBasic)) {
                // Only redirect if the permissions have been set up properly
                return <Redirect to="/home" />;
            }
        }
    }

    return <ApplicationStatusPage application={unitApplication} />;
};
